import { createFileRoute, useRouter } from "@tanstack/react-router";
import { ApiInternal } from "src/api/api_internal";
import { useTranslation } from "react-i18next";
import { Divider } from "src/components/base/divider";
import { useForm, useStore } from "@tanstack/react-form";
import React from "react";
import { Dialog, DialogActions, DialogBody, DialogTitle } from "src/components/base/dialog";
import Form from "src/components/base/form";
import { Description, Field, FieldGroup, Fieldset, Label, Legend, RequiredLabel } from "src/components/base/fieldset";
import { Input } from "src/components/base/input";
import { Button } from "src/components/base/button";
import { Radio, RadioField, RadioGroup } from "src/components/base/radio";
import { Text } from "src/components/base/text";
import { Combobox, ComboboxOption } from "src/components/base/combobox";
import { Heading, Subheading } from "src/components/base/heading";
import { Dropdown, DropdownButton, DropdownItem, DropdownMenu } from "src/components/base/dropdown";
import { ArrowUpIcon, EllipsisVerticalIcon, ArrowDownIcon, TrashIcon, PlusIcon } from "@heroicons/react/20/solid";
import { Table, TableBody, TableCell, TableRow } from "src/components/base/table";

/**
 * The properties for {@link RouteComponent}
 */
export type RouteComponentProps = {};

/** Enum describing the radio buttons in the "add search domain" form */
type InsertPosition = "top" | "bottom" | "before";

/** DNS settings for search domains */
function RouteComponent(props: RouteComponentProps) {
    const [t] = useTranslation("admin-dns-search-domains");

    const router = useRouter();
    const [{ list }, { dns_base_domain }] = Route.useLoaderData();

    const [openAddDialog, setOpenAddDialog] = React.useState(false);
    const [domainQuery, setDomainQuery] = React.useState("");

    const form = useForm({
        defaultValues: {
            domain: "",
            position: "bottom" as InsertPosition,
            before: 0,
        },
        validators: {
            onSubmitAsync: async ({ value }) => {
                if (list.find(({ domain }) => domain === value.domain))
                    return {
                        fields: {
                            domain: t("error.domain-already-exists"),
                        },
                    };

                await ApiInternal.dns.insertSearchDomain({
                    domain: value.domain,
                    index: { top: 0, bottom: list.length, before: value.before }[value.position],
                });

                setOpenAddDialog(false);
                form.reset();
                await router.invalidate({ sync: true });
            },
        },
    });

    const isPositionBefore = useStore(form.store, (store) => store.values.position === "before");

    const canDomainMove = (dir: "up" | "down", index: number) => {
        if (dir === "up" && index === 0) return false;
        if (dir === "down" && index === list.length - 1) return false;
        return true;
    };
    const moveDomain = async (dir: "up" | "down", index: number) => {
        await ApiInternal.dns.moveSearchDomain({ old_index: index, new_index: dir === "up" ? index - 1 : index + 1 });

        await router.invalidate({ sync: true });
    };
    const deleteDomain = async (index: number) => {
        await ApiInternal.dns.removeSearchDomain(index);

        await router.invalidate({ sync: true });
    };

    return (
        <div className={"flex flex-col gap-5"}>
            <div className={"flex items-end justify-between"}>
                <div className={"flex flex-col gap-1"}>
                    <Heading>{t("heading.search-domains")}</Heading>
                    <Text>{t("description.search-domains")}</Text>
                </div>
                <Button className={"h-10"} onClick={() => setOpenAddDialog(true)}>
                    <PlusIcon />
                    {t("button.add-search-domain")}
                </Button>
            </div>

            <Divider />

            <Table className={""}>
                <TableBody>
                    <TableRow>
                        <TableCell>
                            <Subheading>{dns_base_domain}</Subheading>
                        </TableCell>
                        <TableCell />
                    </TableRow>
                    {list.map((domain, index) => (
                        <TableRow>
                            <TableCell>
                                <Subheading>{domain.domain}</Subheading>
                            </TableCell>
                            <TableCell className={"flex flex-row-reverse"}>
                                <Dropdown>
                                    <DropdownButton plain={true}>
                                        <EllipsisVerticalIcon />
                                        <DropdownMenu anchor={"bottom end"}>
                                            <DropdownItem
                                                disabled={!canDomainMove("up", index)}
                                                onClick={() => moveDomain("up", index)}
                                            >
                                                <ArrowUpIcon />
                                                {t("button.move-up")}
                                            </DropdownItem>
                                            <DropdownItem
                                                disabled={!canDomainMove("down", index)}
                                                onClick={() => moveDomain("down", index)}
                                            >
                                                <ArrowDownIcon />
                                                {t("button.move-down")}
                                            </DropdownItem>
                                            <DropdownItem onClick={() => deleteDomain(index)}>
                                                <TrashIcon />
                                                {t("button.delete")}
                                            </DropdownItem>
                                        </DropdownMenu>
                                    </DropdownButton>
                                </Dropdown>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            <Dialog open={openAddDialog} onClose={() => setOpenAddDialog(false)}>
                <DialogTitle>{t("heading.add-search-domain")}</DialogTitle>
                <Form onSubmit={form.handleSubmit}>
                    <DialogBody className={"flex max-w-lg flex-col gap-12"}>
                        <Fieldset>
                            <FieldGroup>
                                <form.Field name={"domain"}>
                                    {(fieldApi) => (
                                        <Field>
                                            <RequiredLabel>{t("label.domain")}</RequiredLabel>
                                            <Input
                                                value={fieldApi.state.value}
                                                onChange={(e) => fieldApi.handleChange(e.target.value)}
                                            />
                                        </Field>
                                    )}
                                </form.Field>
                            </FieldGroup>
                        </Fieldset>
                        <Divider />
                        <Fieldset>
                            <Legend>{t("heading.insert-position")}</Legend>
                            <Text>{t("description.insert-position")}</Text>
                            <form.Field name={"position"}>
                                {(fieldApi) => (
                                    <RadioGroup
                                        name={"position"}
                                        defaultValue={"top"}
                                        value={fieldApi.state.value}
                                        onChange={(e) => {
                                            fieldApi.handleChange(e as InsertPosition);
                                        }}
                                    >
                                        <RadioField>
                                            <Radio value={"top"}></Radio>
                                            <Label>{t("label.insert-top")}</Label>
                                            <Description>{t("description.insert-top")}</Description>
                                        </RadioField>
                                        <RadioField>
                                            <Radio value={"bottom"}></Radio>
                                            <Label>{t("label.insert-bottom")}</Label>
                                            <Description>{t("description.insert-bottom")}</Description>
                                        </RadioField>
                                        <RadioField>
                                            <Radio value={"before"}></Radio>
                                            <Label>{t("label.insert-before")}</Label>
                                            <Description>{t("description.insert-before")}</Description>
                                        </RadioField>
                                    </RadioGroup>
                                )}
                            </form.Field>
                        </Fieldset>
                        <Fieldset disabled={!isPositionBefore}>
                            <FieldGroup>
                                <form.Field name={"before"}>
                                    {(fieldApi) => (
                                        <Field>
                                            <Description>{t("description.select-before")}</Description>
                                            <Combobox
                                                value={fieldApi.state.value}
                                                onChange={(index) => fieldApi.handleChange(index ?? 0)}
                                                onClose={() => setDomainQuery("")}
                                                queryDisplay={(index) => list[index]?.domain ?? ""}
                                                onQueryChange={setDomainQuery}
                                            >
                                                {list
                                                    .map(({ domain }, index) => [domain, index] as const)
                                                    .filter(([domain, _]) =>
                                                        domain.toLowerCase().includes(domainQuery.toLowerCase()),
                                                    )
                                                    .map(([domain, index]) => (
                                                        <ComboboxOption value={index}>{domain}</ComboboxOption>
                                                    ))}
                                            </Combobox>
                                        </Field>
                                    )}
                                </form.Field>
                            </FieldGroup>
                        </Fieldset>
                        <Divider />
                    </DialogBody>
                    <DialogActions>
                        <Button type={"submit"}>{t("button.add-search-domain")}</Button>
                    </DialogActions>
                </Form>
            </Dialog>
        </div>
    );
}

export const Route = createFileRoute("/_admin/a/dns/_dns/search-domains")({
    component: RouteComponent,
    loader: async () => await Promise.all([ApiInternal.dns.getSearchDomains(), ApiInternal.settings.getGeneral()]),
});
